import React, { useState, useMemo, useCallback } from 'react';
import { withVRPUILoading } from '../common/withContextLoading';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Button,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Chip,
  Skeleton,
  Alert,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  FolderOpen as ProjectIcon,
  TableChart as TableIcon,
  Business as ScenarioIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSafeContextData, useContextInitializationGuard } from '../../hooks/useSafeContextHooks';
import { useSupabaseTableMetadata } from '../../hooks/useSupabaseTableMetadata';

export interface SmartSecondarySidebarProps {
  width?: number;
  collapsed?: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
}

interface TableInfo {
  name: string;
  projectName: string;
  scenarioName: string;
  rowCount: number;
  path: string;
  lastModified?: Date;
  type?: string;
  // Enhanced properties for Table Editor mode
  icon?: string;
  isLoaded?: boolean;
  isEmpty?: boolean;
  fullPath?: string;
}

// Tree view node interface for MUI X Tree View
interface VRPTreeNode {
  id: string;
  label: string;
  type: 'project' | 'scenario' | 'table';
  children?: VRPTreeNode[];
  metadata?: {
    count?: number;
    lastModified?: Date;
    tableType?: string;
    path?: string;
  };
}

/**
 * Smart Secondary Sidebar - Supabase Design System Implementation
 * 
 * Built according to exact Supabase specifications:
 * - Width: 280px fixed
 * - Context Header: 56px height
 * - Action Section: 48px height  
 * - Tree View Content Area: Flex remaining space
 * - Background: surface_secondary (#F9FAFB)
 * - Border: 1px solid border_muted (#E5E7EB)
 */
export const SmartSecondarySidebar: React.FC<SmartSecondarySidebarProps> = ({
  collapsed = false,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Use proper context data integration with initialization guards
  const {
    entities,
    loading,
    currentProject,
    projects,
    projectsLoading,
    currentScenario,
    scenarios,
    scenariosLoading,
    contextReady,
    systemReady,
    isInitializing,
    overallLoading
  } = useSafeContextData();
  
  // Context initialization guard for better error handling
  const { 
    canRender, 
    shouldShowLoading, 
    initializationStatus 
  } = useContextInitializationGuard();
  
  // Schema selector state (for context header) - must be declared before using in hook
  const [selectedSchema, setSelectedSchema] = useState<string>('public');
  
  // Get real Supabase table metadata
  const {
    tables: supabaseTables,
    loading: tablesLoading,
    error: tablesError,
    refresh: refreshTables,
    lastUpdated: tablesLastUpdated
  } = useSupabaseTableMetadata(selectedSchema);
  
  // Search state with localStorage persistence
  const [searchTerm, setSearchTerm] = useState(() => {
    const saved = localStorage.getItem('vrp-sidebar-search-term');
    return saved || '';
  });
  
  // Tree view state for MUI X Tree View with URL synchronization
  const [expanded, setExpanded] = useState<string[]>(() => {
    const saved = localStorage.getItem('vrp-tree-expanded');
    return saved ? JSON.parse(saved) : [];
  });
  
  const [selected, setSelected] = useState<string[]>([]);
  
  // Multi-select mode state
  const [multiSelectMode] = useState<boolean>(false);
  
  // Smart Context Switching: Navigation mode detection
  const { navigationMode, contextInfo } = useMemo(() => {
    const pathname = location.pathname;
    
    // Table Editor Mode: Flat table access across projects
    if (pathname.includes('/table-editor') || pathname.includes('/tables') || pathname.includes('/sql-editor')) {
      return {
        navigationMode: 'table-editor' as const,
        contextInfo: {
          title: 'Table Editor',
          description: 'Access all tables across projects',
          searchPlaceholder: 'Search tables...',
          actionButton: 'New Table'
        }
      };
    }
    
    // Projects Mode: Hierarchical project navigation
    if (pathname.includes('/projects') || 
        pathname === '/dashboard' ||
        pathname.includes('/scenarios') ||
        pathname.includes('/vehicles') ||
        pathname.includes('/locations') ||
        pathname.includes('/jobs') ||
        pathname.includes('/products') ||
        pathname.includes('/skills')) {
      return {
        navigationMode: 'projects' as const,
        contextInfo: {
          title: 'Project Hierarchy',
          description: 'Navigate through projects, scenarios, and datasets',
          searchPlaceholder: 'Search projects...',
          actionButton: 'New Project'
        }
      };
    }
    
    // Default fallback
    return {
      navigationMode: 'default' as const,
      contextInfo: {
        title: 'Navigation',
        description: 'Choose a section to explore',
        searchPlaceholder: 'Search...',
        actionButton: 'New Item'
      }
    };
  }, [location.pathname]);
  
  // Convenience flags for backward compatibility
  const isTableEditorMode = navigationMode === 'table-editor';
  const isProjectsMode = navigationMode === 'projects';
  
  // Context-aware titles and descriptions (derived from context switching logic)
  const contextTitle = contextInfo.title;
  const searchPlaceholder = contextInfo.searchPlaceholder;
  const actionButtonText = contextInfo.actionButton;

  // Enhanced tree data structure for projects with complete hierarchy
  const treeData: VRPTreeNode[] = useMemo(() => {
    // Only proceed if we have projects data
    if (!Array.isArray(projects) || projects.length === 0) {
      console.log('🔍 [SmartSecondarySidebar] No projects available for tree data');
      return [];
    }

    console.log('🌳 [SmartSecondarySidebar] Building tree data for', projects.length, 'projects');
    
    const tableTypes = [
      { key: 'vehicles', icon: '🚗', name: 'vehicles' },
      { key: 'locations', icon: '📍', name: 'locations' },
      { key: 'jobs', icon: '📦', name: 'jobs' },
      { key: 'products', icon: '📦', name: 'products' },
      { key: 'skills', icon: '🔧', name: 'skills' },
      { key: 'time_windows', icon: '⏰', name: 'time_windows' }
    ];
    
    return projects.map(project => {
      const projectScenarios = Array.isArray(scenarios) 
        ? scenarios.filter(scenario => scenario && scenario.project_id === project.id)
        : [];
      
      console.log(`📋 [SmartSecondarySidebar] Project "${project.name}" has ${projectScenarios.length} scenarios`);
      
      const scenarioNodes: VRPTreeNode[] = projectScenarios.map(scenario => {
        
        // Build enhanced table nodes with icons and better labeling
        const tableNodes: VRPTreeNode[] = tableTypes
          .map(tableType => {
            // Check if this table type has data for current scenario
            const count = (currentProject?.id === project.id && currentScenario?.id === scenario.id)
              ? (entities?.[tableType.key as keyof typeof entities]?.length ?? 0)
              : 0;
            
            // Always show table types, but indicate if they have data
            return {
              id: `${project.id}-${scenario.id}-${tableType.key}`,
              label: count > 0 
                ? `${tableType.icon} ${tableType.name} (${count} rows)`
                : `${tableType.icon} ${tableType.name}`,
              type: 'table' as const,
              metadata: { 
                count, 
                tableType: tableType.key, 
                path: `/projects/${project.id}/${tableType.name}`,
                isEmpty: count === 0
              }
            };
          });
        
        const totalTables = tableNodes.filter(node => node.metadata?.count && node.metadata.count > 0).length;
        
        return {
          id: `${project.id}-${scenario.id}`,
          label: `📋 ${scenario.name}`,
          type: 'scenario' as const,
          children: tableNodes,
          metadata: { 
            count: totalTables,
            totalRows: tableNodes.reduce((sum, node) => sum + (node.metadata?.count || 0), 0)
          }
        };
      });
      
      const totalScenarios = scenarioNodes.length;
      const totalTables = scenarioNodes.reduce((sum, scenario) => sum + (scenario.metadata?.count || 0), 0);
      
      const projectNode = {
        id: `project-${project.id}`,
        label: `📁 ${project.name}`,
        type: 'project' as const,
        children: scenarioNodes.length > 0 ? scenarioNodes : undefined,
        metadata: { 
          count: totalScenarios,
          totalTables,
          description: project.description || `${totalScenarios} scenarios, ${totalTables} tables`
        }
      };
      
      console.log(`📁 [SmartSecondarySidebar] Project node:`, projectNode);
      return projectNode;
    });
  }, [projects, scenarios, currentProject, currentScenario, entities]);

  // Enhanced flat table list for Table Editor mode - shows real Supabase tables
  const allTables: TableInfo[] = useMemo(() => {
    if (isTableEditorMode) {
      // Use real Supabase table metadata for Table Editor mode
      return supabaseTables.map(table => ({
        name: table.name,
        projectName: 'Supabase',
        scenarioName: table.schema,
        rowCount: table.rowCount,
        path: `/table-editor/${table.name}`,
        type: table.name,
        lastModified: table.lastModified || new Date(),
        icon: table.icon,
        isLoaded: true,
        isEmpty: table.rowCount === 0,
        fullPath: `${table.schema} → ${table.name}`
      }));
    } else {
      // For Projects mode, use the existing project/scenario-based tables
      const tableTypes = [
        { key: 'vehicles', icon: '🚗', name: 'vehicles' },
        { key: 'locations', icon: '📍', name: 'locations' },
        { key: 'jobs', icon: '📦', name: 'jobs' },
        { key: 'products', icon: '📦', name: 'products' },
        { key: 'skills', icon: '🔧', name: 'skills' },
        { key: 'time_windows', icon: '⏰', name: 'time_windows' }
      ];
      
      const tables: TableInfo[] = [];
      
      // Iterate through all projects and scenarios to build comprehensive table list
      projects.forEach(project => {
        const projectScenarios = scenarios.filter(scenario => scenario.project_id === project.id);
        
        projectScenarios.forEach(scenario => {
          tableTypes.forEach(tableType => {
            // For current project/scenario, get actual count; for others, show as available
            const rowCount = (currentProject?.id === project.id && currentScenario?.id === scenario.id)
              ? (entities?.[tableType.key as keyof typeof entities]?.length ?? 0)
              : 0; // Will be loaded when accessed
            
            // Add table entry with comprehensive information
            tables.push({
              name: `${tableType.name} (${project.name} - ${scenario.name})`,
              projectName: project.name,
              scenarioName: scenario.name,
              rowCount,
              path: `/projects/${project.id}/scenarios/${scenario.id}/${tableType.name}`,
              type: tableType.key,
              lastModified: new Date(),
              // Additional metadata for Table Editor mode
              icon: tableType.icon,
              isLoaded: currentProject?.id === project.id && currentScenario?.id === scenario.id,
              isEmpty: rowCount === 0,
              fullPath: `${project.name} → ${scenario.name} → ${tableType.name}`
            });
          });
        });
      });
      
      // Sort by project, then scenario, then table type
      return tables.sort((a, b) => {
        if (a.projectName !== b.projectName) return a.projectName.localeCompare(b.projectName);
        if (a.scenarioName !== b.scenarioName) return a.scenarioName.localeCompare(b.scenarioName);
        return a.name.localeCompare(b.name);
      });
    }
  }, [isTableEditorMode, supabaseTables, projects, scenarios, currentProject, currentScenario, entities]);

  // Search functionality
  const filteredTables = useMemo(() => {
    if (!searchTerm.trim()) return allTables;
    
    const term = searchTerm.toLowerCase();
    return allTables.filter(table =>
      table.name.toLowerCase().includes(term) ||
      table.projectName.toLowerCase().includes(term) ||
      table.scenarioName.toLowerCase().includes(term)
    );
  }, [allTables, searchTerm]);
  
  const filteredTreeData = useMemo(() => {
    if (!searchTerm.trim()) return treeData;
    
    const term = searchTerm.toLowerCase();
    const filterNode = (node: VRPTreeNode): VRPTreeNode | null => {
      const nodeMatches = node.label.toLowerCase().includes(term);
      const filteredChildren = node.children?.map(filterNode).filter(Boolean) || [];
      
      return (nodeMatches || filteredChildren.length > 0) 
        ? { ...node, children: filteredChildren.length > 0 ? filteredChildren : node.children }
        : null;
    };
    
    return treeData.map(filterNode).filter(Boolean) as VRPTreeNode[];
  }, [treeData, searchTerm]);

  // Event handlers
  const handleNewProject = useCallback(() => {
    navigate('/projects/new');
  }, [navigate]);

  const handleTableClick = useCallback((table: TableInfo) => {
    // For table editor mode, always navigate to table-editor routes
    if (isTableEditorMode) {
      // Extract actual table name from the table info
      const tableName = table.type || table.name.split(' ')[0]; // Get base table name
      navigate(`/table-editor/${tableName}`);
    } else {
      navigate(table.path);
    }
  }, [navigate, isTableEditorMode]);

  const handleTreeNodeToggle = useCallback((_event: React.SyntheticEvent, nodeIds: string[]) => {
    setExpanded(nodeIds);
    localStorage.setItem('vrp-tree-expanded', JSON.stringify(nodeIds));
  }, []);
  
  const handleTreeNodeSelect = useCallback((_event: React.SyntheticEvent, nodeIds: string[]) => {
    setSelected(nodeIds);
    
    if (!multiSelectMode && nodeIds.length === 1) {
      const nodeId = nodeIds[0];
      
      if (nodeId.startsWith('project-')) {
        const projectId = nodeId.replace('project-', '');
        navigate(`/projects/${projectId}`);
      } else if (nodeId.includes('-')) {
        const parts = nodeId.split('-');
        if (parts.length === 2) {
          const [projectId] = parts;
          navigate(`/projects/${projectId}/scenarios/${parts[1]}`);
        } else if (parts.length === 3) {
          const [projectId, , tableType] = parts;
          navigate(`/projects/${projectId}/${tableType}`);
        }
      }
    }
  }, [navigate, multiSelectMode]);

  // Enhanced loading states with initialization guards
  const isLoading = shouldShowLoading || overallLoading || (isTableEditorMode && tablesLoading);
  const isFullyReady = canRender && (isTableEditorMode ? !tablesLoading : contextReady);
  const hasData = isTableEditorMode ? (supabaseTables?.length ?? 0) > 0 : (projects?.length ?? 0) > 0;

  // Render functions
  const renderTreeItems = (nodes: VRPTreeNode[]): React.ReactNode => {
    const getNodeIcon = (type: string) => {
      const iconProps = {
        sx: { 
          fontSize: 16,
          mr: 1,
          color: type === 'project' ? '#3ECF8E' : type === 'scenario' ? '#1F2937' : '#6B7280'
        }
      };
      
      switch (type) {
        case 'project': 
          return <ProjectIcon {...iconProps} />;
        case 'scenario': 
          return <ScenarioIcon {...iconProps} />;
        case 'table': 
          return <TableIcon {...iconProps} />;
        default: 
          return null;
      }
    };
    
    return nodes.map((node) => {
      const hasChildren = node.children && node.children.length > 0;
      
      return (
        <TreeItem
          key={node.id}
          itemId={node.id}
          label={
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              py: 0.5,
              minHeight: 32,
            }}>
              {getNodeIcon(node.type)}
              <Typography 
                variant="body2" 
                sx={{ 
                  fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                  fontSize: 14,
                  fontWeight: node.type === 'project' ? 500 : 400,
                  color: '#111827',
                  lineHeight: 1.4,
                }}
              >
                {node.label}
              </Typography>
              {node.metadata?.count !== undefined && node.metadata.count > 0 && (
                <Chip
                  size="small"
                  label={node.metadata.count}
                  sx={{
                    height: 20,
                    fontSize: 11,
                    ml: 'auto',
                    bgcolor: '#ECFDF5',
                    color: '#111827',
                    '& .MuiChip-label': { px: 1 }
                  }}
                />
              )}
            </Box>
          }
        >
          {hasChildren && renderTreeItems(node.children!)}
        </TreeItem>
      );
    });
  };

  const renderProjectHierarchy = () => {
    // Show initialization loading state
    if (isLoading) {
      return (
        <Box sx={{ flex: 1, p: 2 }}>
          {isInitializing && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Initializing contexts...
              </Typography>
            </Box>
          )}
          {[1, 2, 3].map(i => (
            <Skeleton key={i} variant="rectangular" height={32} sx={{ mb: 1 }} />
          ))}
        </Box>
      );
    }

    if (!hasData) {
      console.log('🚫 [SmartSecondarySidebar] No data available:', {
        projects: projects?.length ?? 0,
        scenarios: scenarios?.length ?? 0,
        isTableEditorMode,
        isProjectsMode,
        hasData
      });
      
      return (
        <Box sx={{ 
          flex: 1, 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
        }}>
          <ProjectIcon sx={{ fontSize: 48, color: '#3ECF8E', mb: 2, opacity: 0.6 }} />
          <Typography variant="h6" sx={{ 
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 16,
            fontWeight: 600,
            color: '#111827',
            mb: 1 
          }}>
            {isTableEditorMode ? 'No database tables' : 'No projects yet'}
          </Typography>
          <Typography variant="body2" sx={{ 
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 14,
            color: '#6B7280',
            mb: 2,
            maxWidth: 200 
          }}>
            {isTableEditorMode 
              ? 'No accessible database tables found.' 
              : 'Create your first project to start organizing your vehicle routing scenarios.'
            }
          </Typography>
          {/* Debug info in development */}
          {process.env.NODE_ENV === 'development' && (
            <Typography variant="caption" sx={{ 
              fontSize: 12,
              color: '#9CA3AF',
              mb: 3,
              fontFamily: 'monospace'
            }}>
              Debug: {projects?.length ?? 0} projects, mode: {navigationMode}
            </Typography>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleNewProject}
            sx={{
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              textTransform: 'none',
              borderRadius: '6px',
              bgcolor: '#3ECF8E',
              color: 'white',
              px: 3,
              py: 1,
              '&:hover': {
                bgcolor: '#10B981',
              }
            }}
          >
            {isTableEditorMode ? 'Browse Tables' : 'Create Project'}
          </Button>
        </Box>
      );
    }

    const dataToRender = searchTerm ? filteredTreeData : treeData;
    
    return (
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        <SimpleTreeView
          expandedItems={expanded}
          selectedItems={selected}
          onExpandedItemsChange={handleTreeNodeToggle}
          onSelectedItemsChange={handleTreeNodeSelect}
          multiSelect={multiSelectMode}
          slots={{
            collapseIcon: () => <ExpandMoreIcon sx={{ color: '#6B7280' }} />,
            expandIcon: () => <ChevronRightIcon sx={{ color: '#6B7280' }} />,
          }}
          sx={{
            '& .MuiTreeItem-content': {
              padding: '6px 12px',
              borderRadius: '4px',
              margin: '1px 0',
              transition: 'all 0.2s ease',
              minHeight: 32,
              '&:hover': {
                backgroundColor: '#F3F4F6',
              },
              '&.Mui-selected': {
                backgroundColor: '#D1FAE5',
                color: '#3ECF8E',
                '&:hover': {
                  backgroundColor: '#D1FAE5',
                },
              },
            },
            '& .MuiTreeItem-group': {
              marginLeft: 16,
              paddingLeft: 8,
              borderLeft: '1px solid #E5E7EB',
            },
          }}
        >
          {renderTreeItems(dataToRender)}
        </SimpleTreeView>
      </Box>
    );
  };

  const renderTableEditor = () => {
    if (isLoading) {
      return (
        <Box sx={{ flex: 1, p: 2 }}>
          {isInitializing && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
              <CircularProgress size={16} sx={{ mr: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Loading database tables...
              </Typography>
            </Box>
          )}
          {[1, 2, 3, 4, 5, 6].map(i => (
            <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: '6px' }} />
          ))}
        </Box>
      );
    }

    // Show error state for table loading
    if (tablesError) {
      const isRPCError = tablesError.includes('get_table_metadata') || tablesError.includes('does not exist');
      
      return (
        <Box sx={{ flex: 1, p: 2 }}>
          <Alert 
            severity="error" 
            action={
              <Button 
                color="inherit" 
                size="small" 
                onClick={refreshTables}
                startIcon={<RefreshIcon />}
              >
                Retry
              </Button>
            }
            sx={{ mb: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              {isRPCError ? 'Database Function Missing' : 'Table Metadata Error'}
            </Typography>
            <Typography variant="body2" sx={{ fontSize: 12, mb: 1 }}>
              {isRPCError 
                ? 'The get_table_metadata database function is not available.'
                : `Failed to load table metadata: ${tablesError}`
              }
            </Typography>
            <Typography variant="caption" sx={{ fontSize: 11, color: '#6B7280' }}>
              {isRPCError 
                ? 'Contact your database administrator to create the required function.'
                : 'Check your database connection and try again.'
              }
            </Typography>
          </Alert>
        </Box>
      );
    }

    if (filteredTables.length === 0 && !searchTerm) {
      return (
        <Box sx={{ 
          flex: 1, 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
        }}>
          <TableIcon sx={{ fontSize: 48, color: '#3ECF8E', mb: 2, opacity: 0.6 }} />
          <Typography variant="h6" sx={{ 
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 16,
            fontWeight: 600,
            color: '#111827',
            mb: 1 
          }}>
            No database tables found
          </Typography>
          <Typography variant="body2" sx={{ 
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 14,
            color: '#6B7280',
            mb: 3,
            maxWidth: 200 
          }}>
            No accessible tables found in the public schema. Check your database permissions.
          </Typography>
        </Box>
      );
    }

    if (filteredTables.length === 0 && searchTerm) {
      return (
        <Box sx={{ 
          flex: 1, 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 3,
          textAlign: 'center',
        }}>
          <SearchIcon sx={{ fontSize: 48, color: '#6B7280', mb: 2, opacity: 0.6 }} />
          <Typography variant="h6" sx={{ 
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 16,
            fontWeight: 600,
            color: '#111827',
            mb: 1 
          }}>
            No tables found
          </Typography>
          <Typography variant="body2" sx={{ 
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 14,
            color: '#6B7280',
            maxWidth: 200 
          }}>
            Try adjusting your search term to find tables.
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {/* Enhanced Table List with Better Visual Design */}
        <List dense sx={{ padding: 0 }}>
          {filteredTables.map((table, index) => {

            // Status indicators based on data
            const getStatusColor = (table: TableInfo) => {
              if (!table.isLoaded) return '#9CA3AF'; // text_muted - not loaded
              if (table.isEmpty) return '#F59E0B'; // warning - empty
              return '#10B981'; // success - has data
            };

            const getStatusText = (table: TableInfo) => {
              if (!table.isLoaded) return 'Not loaded';
              if (table.isEmpty) return 'Empty';
              return 'Ready';
            };

            return (
              <ListItemButton
                key={`${table.projectName}-${table.scenarioName}-${table.type}-${index}`}
                onClick={() => handleTableClick(table)}
                sx={{ 
                  py: 0.75,
                  px: 1.5,
                  minHeight: 32,
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'action.selected',
                  }
                }}
              >
                <ListItemIcon sx={{ minWidth: 32, mr: 1.5, color: 'text.secondary' }}>
                  <TableIcon sx={{ fontSize: 18 }} />
                </ListItemIcon>
                
                <ListItemText
                  primary={
                    <Typography variant="body2" sx={{ 
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                      fontSize: 13,
                      fontWeight: 400,
                      color: 'text.primary',
                      lineHeight: 1.5,
                    }}>
                      {isTableEditorMode ? table.name : table.type}
                    </Typography>
                  }
                  secondary={null}
                  sx={{ my: 0 }}
                />
              </ListItemButton>
            );
          })}
        </List>
        
        {/* Summary footer */}
        {filteredTables.length > 0 && (
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: '#F9FAFB', 
            borderRadius: '6px',
            border: '1px solid #E5E7EB',
          }}>
            <Typography variant="caption" sx={{
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: 12,
              color: '#6B7280',
              display: 'block',
              textAlign: 'center',
            }}>
              {isTableEditorMode ? (
                <>
                  {filteredTables.length} Supabase tables • {filteredTables.reduce((sum, t) => sum + t.rowCount, 0)} total rows
                  {tablesLastUpdated && (
                    <><br />Last updated: {tablesLastUpdated.toLocaleTimeString()}</>
                  )}
                </>
              ) : (
                `Showing ${filteredTables.length} tables across ${new Set(filteredTables.map(t => t.projectName)).size} projects`
              )}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        width: collapsed ? 56 : 280, // Responsive width: 56px collapsed, 280px expanded per Supabase spec
        height: '100%',
        bgcolor: '#F9FAFB', // surface_secondary per Supabase spec
        borderRight: '1px solid #E5E7EB', // border_muted per Supabase spec
        display: 'flex',
        flexDirection: 'column',
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        minWidth: collapsed ? 56 : 280, // Ensure minimum width to prevent layout collapse
      }}
    >
      {/* Context Header Section - 56px height per Supabase spec */}
      {!collapsed ? (
        <Box sx={{ 
          height: 56, // Fixed 56px height per Supabase spec
          padding: '16px', // 16px padding per Supabase spec
          borderBottom: '1px solid #E5E7EB', // border_muted per Supabase spec
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 16,
            fontWeight: 600, 
            color: '#111827', // text_primary per Supabase spec
            lineHeight: 1.4,
            mb: 0.5,
          }}
        >
          {contextTitle}
        </Typography>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={selectedSchema}
            onChange={(e) => setSelectedSchema(e.target.value)}
            sx={{
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: 14,
              color: '#6B7280', // text_secondary per Supabase spec
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
            }}
          >
            <MenuItem value="public">public schema</MenuItem>
            <MenuItem value="vrp">vrp schema</MenuItem>
          </Select>
        </FormControl>
        </Box>
      ) : (
        // Collapsed header: just show icon
        <Box sx={{ 
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderBottom: '1px solid #E5E7EB',
        }}>
          {isProjectsMode ? (
            <ProjectIcon sx={{ fontSize: 20, color: '#3ECF8E' }} />
          ) : (
            <TableIcon sx={{ fontSize: 20, color: '#3ECF8E' }} />
          )}
        </Box>
      )}

      {/* Action Section - 48px height per Supabase spec */}
      {!collapsed && (
        <Box sx={{ 
          height: 48, // Fixed 48px height per Supabase spec
          padding: '12px 16px', // 12px vertical, 16px horizontal per Supabase spec
          borderBottom: '1px solid #E5E7EB', // border_muted per Supabase spec
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={navigationMode === 'projects' ? handleNewProject : () => navigate('/table-editor/new')}
          sx={{
            fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
            fontSize: 14,
            fontWeight: 500,
            textTransform: 'none',
            borderRadius: '6px',
            bgcolor: '#3ECF8E',
            color: 'white',
            px: 2,
            py: 0.5,
            minHeight: 32,
            '&:hover': {
              bgcolor: '#10B981',
            }
          }}
        >
          {actionButtonText}
        </Button>
        
        {/* Refresh button for Table Editor mode */}
        {isTableEditorMode && (
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={refreshTables}
            disabled={tablesLoading}
            sx={{
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: 14,
              fontWeight: 500,
              textTransform: 'none',
              borderRadius: '6px',
              borderColor: '#E5E7EB',
              color: '#6B7280',
              px: 2,
              py: 0.5,
              minHeight: 32,
              minWidth: 32,
              '&:hover': {
                borderColor: '#3ECF8E',
                color: '#3ECF8E',
              }
            }}
          >
            {tablesLoading ? <CircularProgress size={16} /> : null}
          </Button>
        )}
        
        <TextField
          size="small"
          placeholder={searchPlaceholder}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: 14,
              borderRadius: '6px',
              bgcolor: '#FFFFFF', // surface_primary per Supabase spec
              '& fieldset': {
                borderColor: '#E5E7EB', // border_muted per Supabase spec
              },
              '&:hover fieldset': {
                borderColor: '#D1D5DB',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#3ECF8E',
                borderWidth: 2,
              },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 16, color: '#6B7280' }} />
              </InputAdornment>
            ),
          }}
        />
        </Box>
      )}

      {/* Development Context Status Indicator (only visible when not fully ready) */}
      {!collapsed && !isFullyReady && process.env.NODE_ENV === 'development' && (
        <Box sx={{ 
          px: 2, 
          py: 1, 
          bgcolor: 'warning.light', 
          borderBottom: '1px solid #E5E7EB',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <CircularProgress size={12} />
          <Typography variant="caption" color="text.secondary">
            Context Status: {initializationStatus.vrp ? '✓' : '○'} VRP, {initializationStatus.project ? '✓' : '○'} Project, {initializationStatus.scenario ? '✓' : '○'} Scenario
          </Typography>
        </Box>
      )}

      {/* Tree View Content Area - Flex remaining space */}
      {collapsed ? (
        // Collapsed state: show minimal icon-based navigation
        <Box sx={{ 
          flex: 1, 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center',
          pt: 2,
          gap: 2,
          overflow: 'hidden'
        }}>
          {/* Show only essential navigation icons in collapsed state */}
          {isProjectsMode && projects.slice(0, 3).map((project) => (
            <Box
              key={project.id}
              onClick={() => navigate(`/projects/${project.id}`)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              <ProjectIcon sx={{ fontSize: 16, color: '#6B7280' }} />
            </Box>
          ))}
          {isTableEditorMode && allTables.slice(0, 4).map((table, index) => (
            <Box
              key={`${table.name}-${index}`}
              onClick={() => handleTableClick(table)}
              sx={{
                width: 32,
                height: 32,
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                backgroundColor: 'transparent',
                '&:hover': {
                  backgroundColor: '#F3F4F6',
                },
              }}
            >
              <TableIcon sx={{ fontSize: 16, color: '#6B7280' }} />
            </Box>
          ))}
        </Box>
      ) : (
        // Expanded state: show full content
        isProjectsMode ? renderProjectHierarchy() : renderTableEditor()
      )}
    </Box>
  );
};

// Apply loading boundary for VRP UI context dependency
const SmartSecondarySidebarWithLoading = withVRPUILoading(SmartSecondarySidebar, {
  loadingMessage: 'Loading navigation...',
  skeletonVariant: 'navigation',
  skeletonCount: 8,
  minLoadTime: 200,
});

export default SmartSecondarySidebarWithLoading;